import { Combobox as ArkCombobox } from '@ark-ui/react';
import { chakra } from '@chakra-ui/react';

export interface ComboboxItem {
    value: string;
    label: string;
}

export const ComboboxRoot = chakra(ArkCombobox.Root, {
    base: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1',
    },
});

export const ComboboxControl = chakra(ArkCombobox.Control, {});

export const ComboboxInputElement = chakra(
    ArkCombobox.Input,
    {
        base: {
            bg: 'rgba(0, 0, 0, 0.02)',
            outlineColor: '#D6D6D6',
            borderColor: '#D6D6D6',
            _focusVisible: { outlineColor: '#D6D6D6', borderColor: '#D6D6D6' },
            _placeholder: { color: '#213547' },
        },
    },
    { forwardAsChild: true },
);

export const ComboboxTrigger = chakra(ArkCombobox.Trigger, {}, { forwardAsChild: true });

export const ComboboxContent = chakra(ArkCombobox.Content, {
    base: {
        borderRadius: 'md',
    },
});

export const ComboboxItem = chakra(ArkCombobox.Item, {
    base: {
        bg: { base: 'white', _dark: 'purple.800' },
        _hover: {
            bg: 'gray.subtle',
            cursor: 'pointer',
        },
        _selected: {
            bg: 'gray.subtle',
        },
        _highlighted: {
            bg: 'gray.subtle',
            cursor: 'pointer',
        },
    },
});

export const ComboboxPositioner = chakra(ArkCombobox.Positioner);

export const ComboboxClearTrigger = chakra(ArkCombobox.ClearTrigger, {}, { forwardAsChild: true });

import { matchSorter } from 'match-sorter';
import { createListCollection } from '@ark-ui/react';
import { useMemo, useRef, useState } from 'react';

interface Item {
    value: string;
    label: string;
}

interface UseComboboxBaseProps<T extends Item> {
    items: T[];
    filterItems?: (value: string) => void;
    value: T | undefined;
    onChange: (value: string | undefined) => void;
    onValueInput?: (value: string) => void;
    name: string;
    creatable: boolean;
    clearable: boolean;
}

export function useComboboxBase<T extends Item>({
    items,
    filterItems,
    value,
    onChange,
    onValueInput,
    creatable,
    clearable,
}: UseComboboxBaseProps<T>) {
    const [inputValue, setInputValue] = useState<string>('');
    const inputRef = useRef<HTMLInputElement | null>(null);

    const collection = useMemo(() => {
        if (!filterItems) {
            const filteredItems = matchSorter(items, inputValue || '', { keys: ['label'] });
            return createListCollection<unknown>({ items: filteredItems });
        }
        return createListCollection<unknown>({ items: items });
    }, [items, inputValue, filterItems]);

    const hasSelectedValue = !!value && inputValue.length === 0;
    const selectedLabel = value?.label || value?.value || '';
    const displayValue = hasSelectedValue ? selectedLabel : inputValue;
    const isClearable = clearable && ((creatable && inputValue) || !!value);

    const moveCaretToEnd = () => {
        if (!hasSelectedValue || !inputRef.current) {
            return;
        }

        const length = selectedLabel.length;
        requestAnimationFrame(() => {
            inputRef.current?.setSelectionRange(length, length);
        });
    };

    const handleValueChange = (newValue: string[]) => {
        const [val] = newValue;
        onChange(val);
        setInputValue('');
    };

    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
        const nextInputValue = (e.target as HTMLInputElement).value;

        // In creatable mode, use the raw input value. In non-creatable mode, when a value is
        // selected and the user types, extract only the newly typed portion (not including the selected label).
        const nextValue =
            creatable || !hasSelectedValue || !nextInputValue.startsWith(selectedLabel)
                ? nextInputValue
                : nextInputValue.slice(selectedLabel.length);

        filterItems?.(nextValue);
        setInputValue(nextValue);
        onValueInput?.(nextValue);
    };

    const handleBlur = () => {
        if (!creatable) {
            setInputValue('');
        }
    };

    return {
        inputRef,
        collection,
        hasSelectedValue,
        selectedLabel,
        displayValue,
        isClearable,
        inputValue,
        setInputValue,
        moveCaretToEnd,
        handleValueChange,
        handleInput,
        handleBlur,
    };
}
